// Mirrors the room category -> section -> LookupListGroupTitle wiring in
// TOPS-WEB-NZ's InternalDescriptionPopupApp.js / InternalDescriptionPopup.html.

export type QuickChoiceSelectionMode = 'single' | 'multi' | 'text';

export interface QuickChoiceSection {
  label: string;
  /** Unique key in the stored selection map (per room instance, e.g. Bathroom2Fixtures). */
  fieldGroup: string;
  /** Key into INTERNAL_DESCRIPTION_OPTIONS for the button choices; falls back to fieldGroup when omitted. */
  optionsGroup?: string;
  mode: QuickChoiceSelectionMode;
  /** When set, this section is only shown once fieldGroup below has a value (e.g. Garage Configuration/No. of Cars). */
  showWhenSelected?: string;
  /** Data-driven cascade (Garage/Additional Garage): only show once the named field's selected option has parameter1/parameter2 === '1' (from the lookup list itself). */
  showWhenParameter?: { field: string; group: string; parameter: 'parameter1' | 'parameter2' };
}

export interface QuickChoiceCategory {
  key: string;
  label: string;
  sections: QuickChoiceSection[];
  /** Renders an editable generated-sentence summary instead of option buttons (matches the legacy "Finish" tab). */
  isSummary?: boolean;
  /** Spans the full nav rail width instead of sharing a row (More Rooms/Chattels/Other/Finish, matching tops-web-nz). */
  fullWidth?: boolean;
}

const floorAndFixtures = (prefix: string, fixturesKey: string): QuickChoiceSection[] => [
  { label: 'Floor Coverings', fieldGroup: `${prefix}FloorCovering`, mode: 'single' },
  { label: 'Fixtures and Fittings', fieldGroup: fixturesKey, mode: 'multi' },
];

// Kitchen/Bathroom/PowderRoom/Bedroom instances share one options list per DB lookup group,
// but each instance needs its own storage key so selections don't leak between rooms.
const kitchenSections = (prefix: string): QuickChoiceSection[] => [
  { label: 'Floor Coverings', fieldGroup: `${prefix}FloorCovering`, optionsGroup: 'FloorCoveringsKitchen', mode: 'single' },
  { label: 'Cupboards', fieldGroup: `${prefix}Cupboards`, optionsGroup: 'Cupboards', mode: 'multi' },
  { label: 'Benchtops', fieldGroup: `${prefix}Benchtops`, optionsGroup: 'Benchtops', mode: 'single' },
  { label: 'Fixtures and Fittings', fieldGroup: `${prefix}Fixtures`, optionsGroup: 'KitchenFixtures', mode: 'multi' },
];

const powderRoomSections = (prefix: string): QuickChoiceSection[] => [
  { label: 'Floor Coverings', fieldGroup: `${prefix}FloorCovering`, optionsGroup: 'PowderRoomFloorCovering', mode: 'single' },
  { label: 'Fixtures and Fittings', fieldGroup: `${prefix}Fixtures`, optionsGroup: 'PowderRoomFixtures', mode: 'multi' },
];

const bathroomSections = (prefix: string): QuickChoiceSection[] => [
  { label: 'Floor Coverings', fieldGroup: `${prefix}FloorCovering`, optionsGroup: 'BathroomFloorCoverings', mode: 'single' },
  { label: 'Fixtures and Fittings', fieldGroup: `${prefix}Fixtures`, optionsGroup: 'BathroomFixtures', mode: 'multi' },
];

const bedroomSections = (prefix: string): QuickChoiceSection[] => [
  { label: 'Floor Coverings', fieldGroup: `${prefix}FloorCovering`, optionsGroup: 'BedroomFloorCovering', mode: 'single' },
  { label: 'Fixtures and Fittings', fieldGroup: `${prefix}Fixtures`, optionsGroup: 'BedroomFixtures', mode: 'multi' },
  { label: 'Ensuite', fieldGroup: `${prefix}Ensuite`, optionsGroup: 'Ensuite', mode: 'single' },
  // Tapping Ensuite expands this nested mini-section (reuses the Bathroom lookup lists — an ensuite is a small bathroom).
  { label: 'Ensuite Floor Coverings', fieldGroup: `${prefix}EnsuiteFloorCovering`, optionsGroup: 'BathroomFloorCoverings', mode: 'single', showWhenSelected: `${prefix}Ensuite` },
  { label: 'Ensuite Fixtures and Fittings', fieldGroup: `${prefix}EnsuiteFixtures`, optionsGroup: 'BathroomFixtures', mode: 'multi', showWhenSelected: `${prefix}Ensuite` },
];

export const INTERNAL_DESCRIPTION_CATEGORIES: QuickChoiceCategory[] = [
  { key: 'Entry', label: 'Entry', sections: floorAndFixtures('Entry', 'EntryFixture') },
  { key: 'Hallway', label: 'Hallway', sections: floorAndFixtures('Hallway', 'HallwayFixture') },
  { key: 'Lounge', label: 'Lounge', sections: floorAndFixtures('Lounge', 'LoungeFixtures') },
  { key: 'Living', label: 'Living', sections: floorAndFixtures('Living', 'LivingFixture') },
  { key: 'Media', label: 'Media', sections: floorAndFixtures('Media', 'MediaFixture') },
  { key: 'Family', label: 'Family', sections: floorAndFixtures('Family', 'FamilyFixture') },
  { key: 'Kitchen', label: 'Kitchen', sections: kitchenSections('Kitchen') },
  { key: 'Dining', label: 'Dining', sections: floorAndFixtures('Dining', 'DiningFixture') },
  { key: 'Kitchen2', label: 'Kitchen 2', sections: kitchenSections('Kitchen2') },
  { key: 'Meals', label: 'Meals', sections: floorAndFixtures('Meals', 'MealsFixture') },
  { key: 'PowderRoom', label: 'Powder Room', sections: powderRoomSections('PowderRoom') },
  { key: 'Bathroom1', label: 'Bathroom 1', sections: bathroomSections('Bathroom1') },
  { key: 'PowderRoom2', label: 'Powder Room 2', sections: powderRoomSections('PowderRoom2') },
  { key: 'Bathroom2', label: 'Bathroom 2', sections: bathroomSections('Bathroom2') },
  { key: 'PowderRoom3', label: 'Powder Room 3', sections: powderRoomSections('PowderRoom3') },
  { key: 'Bathroom3', label: 'Bathroom 3', sections: bathroomSections('Bathroom3') },
  {
    key: 'Laundry',
    label: 'Laundry',
    sections: [
      { label: 'Floor Coverings', fieldGroup: 'LaundryFloor', mode: 'single' },
      { label: 'Location', fieldGroup: 'LaundryLocation', mode: 'single' },
      { label: 'Fixtures and Fittings', fieldGroup: 'LaundryFixture', mode: 'multi' },
    ],
  },
  { key: 'Study', label: 'Study', sections: floorAndFixtures('Study', 'StudyFixture') },
  {
    key: 'OutdoorArea',
    label: 'Outdoor Area',
    sections: [{ label: 'Outdoor Area', fieldGroup: 'OutdoorArea', mode: 'multi' }],
  },
  { key: 'Rumpus', label: 'Rumpus', sections: floorAndFixtures('Rumpus', 'RumpusFixture') },
  {
    key: 'SeparateToilets',
    label: 'Separate Toilets',
    sections: [{ label: 'Count', fieldGroup: 'Count', mode: 'single' }],
  },
  { key: 'Bedroom1', label: 'Bedroom 1', sections: bedroomSections('Bedroom1') },
  { key: 'Bedroom2', label: 'Bedroom 2', sections: bedroomSections('Bedroom2') },
  { key: 'Bedroom3', label: 'Bedroom 3', sections: bedroomSections('Bedroom3') },
  { key: 'Bedroom4', label: 'Bedroom 4', sections: bedroomSections('Bedroom4') },
  { key: 'Bedroom5', label: 'Bedroom 5', sections: bedroomSections('Bedroom5') },
  { key: 'Bedroom6', label: 'Bedroom 6', sections: bedroomSections('Bedroom6') },
  {
    key: 'Garage',
    label: 'Garage',
    sections: [
      { label: 'Garage Type', fieldGroup: 'GarageType', mode: 'single' },
      { label: 'Configuration', fieldGroup: 'GarageConfiguration', mode: 'single', showWhenParameter: { field: 'GarageType', group: 'GarageType', parameter: 'parameter1' } },
      { label: 'No. of Cars', fieldGroup: 'GarageNoOfCars', mode: 'single', showWhenParameter: { field: 'GarageType', group: 'GarageType', parameter: 'parameter2' } },
      { label: 'Fixtures and Fittings', fieldGroup: 'GarageFixturesAndFittings', mode: 'multi' },
      { label: 'Additional Garage Type', fieldGroup: 'AdditionalGarageType', mode: 'single' },
      { label: 'Additional Configuration', fieldGroup: 'AdditionalGarageConfiguration', mode: 'single', showWhenParameter: { field: 'AdditionalGarageType', group: 'AdditionalGarageType', parameter: 'parameter1' } },
      { label: 'Additional No. of Cars', fieldGroup: 'AdditionalGarageNoOfCars', mode: 'single', showWhenParameter: { field: 'AdditionalGarageType', group: 'AdditionalGarageType', parameter: 'parameter2' } },
      { label: 'Additional Fixtures and Fittings', fieldGroup: 'AdditionalGarageFixturesAndFittings', mode: 'multi' },
    ],
  },
  {
    key: 'MoreRooms',
    label: 'More Rooms',
    sections: [{ label: 'More Rooms', fieldGroup: 'MoreRooms', mode: 'multi' }],
    fullWidth: true,
  },
  {
    key: 'Chattels',
    label: 'Chattels',
    sections: [
      { label: 'Quality', fieldGroup: 'ChattelsQuality', mode: 'single' },
      { label: 'Condition', fieldGroup: 'ChattelsCondition', mode: 'single' },
      { label: 'Chattels', fieldGroup: 'ChattelsSelection', mode: 'multi' },
    ],
    fullWidth: true,
  },
  {
    key: 'Other',
    label: 'Other',
    sections: [{ label: 'Other', fieldGroup: 'Other', mode: 'text' }],
    fullWidth: true,
  },
  {
    key: 'Finish',
    label: 'Finish',
    sections: [],
    isSummary: true,
    fullWidth: true,
  },
];
